import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { fetchUserProfile } from '../services/api';
import { UserProfile } from '../types';

interface UserContextValue {
  userDetails: UserProfile | null;
  refreshUserDetails: () => Promise<void>;
  isLoading: boolean;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userDetails, setUserDetails] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadUserDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const profile = await fetchUserProfile();
      setUserDetails(profile);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUserDetails();
  }, [loadUserDetails]);

  const value = useMemo(
    () => ({
      userDetails,
      refreshUserDetails: loadUserDetails,
      isLoading
    }),
    [loadUserDetails, isLoading, userDetails]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
};

export default UserContext;
